import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, withModifiers as _withModifiers, withCtx as _withCtx, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "condition-group-builder" }
const _hoisted_2 = { class: "condition-group before" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "add-label" }

import { ConditionGroupViewModel } from './models/conditionGroupViewModel';
import { IConditionsManager } from './models/conditionsManager';
import AddCondition from './AddCondition.vue';
import ConditionButton from './ConditionButton.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import JoinOperatorPicker from '@/features/contacts/components/JoinOperatorPicker.vue';
import { OperatorType } from '@/features/contacts/models/operatorTypes';
import { PropType, ref, watch, computed } from 'vue';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';

export interface ConditionGroupUpdate {
  conditionGroup: ConditionGroupViewModel;
  invalid: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ConditionGroupBuilder',
  props: {
  value: {
    type: Object as PropType<ConditionGroupViewModel>,
    required: true,
  },
  manager: {
    type: Object as PropType<IConditionsManager>,
    required: true,
  },
  nested: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  context: {
    type: String,
    default: 'filter',
  },
},
  emits: ["updated", "config-opened", "config-closed"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const conditionGroup = ref<ConditionGroupViewModel>({ type: 'and', conditions: [] });
const operatorButtonHover = ref(false);

watch(
  () => props.value,
  (value: ConditionGroupViewModel) => (conditionGroup.value = value),
  { immediate: true },
);

const numberOfConditions = computed(() => {
  if (!conditionGroup.value?.conditions) {
    return 0;
  }

  return conditionGroup.value.conditions.length;
});

function getConditionKey(index: number, item: ConditionViewModel | ConditionGroupViewModel) {
  if (isCondition(item)) {
    return `${index}_${item.attribute}_${item.operation}_${item.value}`;
  }

  return `${index}_group`;
}

function isLastConditionInList(index: number) {
  return index === numberOfConditions.value - 1;
}

function isCondition(item: ConditionViewModel | ConditionGroupViewModel): item is ConditionViewModel {
  return ConditionGroup.isCondition(item);
}

function showMakeFilterGroupOption(item: ConditionViewModel | ConditionGroupViewModel) {
  return !props.nested && isCondition(item);
}

function showAddToFilterGroupOption(item: ConditionViewModel | ConditionGroupViewModel, index: number) {
  return props.nested && isCondition(item) && index === numberOfConditions.value - 1;
}

function onConditionAdded(newItem: ConditionViewModel) {
  newItem.active = true;
  newItem.invalid = false;
  newItem.isNew = true;
  conditionGroup.value.conditions.push(newItem);
}

function onConditionUpdated(index: number, item: ConditionViewModel) {
  item.active = false;
  conditionGroup.value.conditions[index] = item;

  emitUpdatedEvent();
}

function onConditionDeleted(index: number) {
  conditionGroup.value.conditions.splice(index, 1);

  emitUpdatedEvent();
}

function makeFilterGroup(index: number, item: ConditionViewModel, newItem: ConditionViewModel) {
  // replace the condition with a condition group containing existing and new item
  newItem.active = true;
  conditionGroup.value.conditions[index] = {
    type: 'and',
    conditions: [item, newItem],
  };
}

function addToFilterGroup(index: number, newItem: ConditionViewModel) {
  newItem.active = true;
  conditionGroup.value.conditions.push(newItem);
}

function onGroupUpdated() {
  // a child group has been updated - check if any groups have only got one condition left,
  // if so convert them back to a condition from a filter group
  conditionGroup.value.conditions = conditionGroup.value.conditions.map((c: ConditionGroupViewModel, index: number) => {
    if (isCondition(c)) {
      c.active = false;
      return c;
    }

    c = c as ConditionGroupViewModel;
    if (c.conditions.length === 1) {
      return c.conditions[0];
    }

    return c;
  });

  emitUpdatedEvent();
}

function operatorTypeUpdated(operatorType: OperatorType) {
  conditionGroup.value.type = operatorType;
  onGroupUpdated();
}

function updateOperatorHoverState(hover: boolean) {
  operatorButtonHover.value = hover;
}

function hasAnyInvalidConditions() {
  return ConditionGroup.isGroupInvalid(conditionGroup.value);
}

function emitUpdatedEvent() {
  if (props.nested) {
    emit('updated', conditionGroup.value);
  } else {
    emit('updated', {
      conditionGroup: conditionGroup.value,
      invalid: hasAnyInvalidConditions(),
    } as ConditionGroupUpdate);
  }
}

return (_ctx: any,_cache: any) => {
  const _component_ConditionGroupBuilder = _resolveComponent("ConditionGroupBuilder", true)!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _renderSlot(_ctx.$slots, "before")
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(conditionGroup.value.conditions, (item, index) => {
      return (_openBlock(), _createElementBlock("div", {
        class: "condition-group",
        key: getConditionKey(index, item)
      }, [
        (!isCondition(item))
          ? (_openBlock(), _createBlock(_component_ConditionGroupBuilder, {
              key: 0,
              value: item,
              manager: __props.manager,
              nested: true,
              disabled: __props.disabled,
              onUpdated: onGroupUpdated,
              context: __props.context
            }, null, 8, ["value", "manager", "disabled", "context"]))
          : _createCommentVNode("", true),
        (isCondition(item))
          ? (_openBlock(), _createBlock(ConditionButton, {
              key: 1,
              value: item,
              manager: __props.manager,
              disabled: __props.disabled,
              onDeleted: ($event: any) => (onConditionDeleted(index)),
              onUpdated: (updatedItem) => onConditionUpdated(index, updatedItem),
              onConfigOpened: _cache[0] || (_cache[0] = ($event: any) => (emit('config-opened'))),
              onConfigClosed: _cache[1] || (_cache[1] = ($event: any) => (emit('config-closed'))),
              class: _normalizeClass({ left: !__props.nested || index === 0 })
            }, null, 8, ["value", "manager", "disabled", "onDeleted", "onUpdated", "class"]))
          : _createCommentVNode("", true),
        (showMakeFilterGroupOption(item))
          ? (_openBlock(), _createBlock(AddCondition, {
              key: 2,
              disabled: __props.disabled || hasAnyInvalidConditions(),
              tooltip: 
          !hasAnyInvalidConditions() ? `Make ${__props.context} group` : `Fix invalid ${__props.context}s before adding another`
        ,
              "available-conditions": __props.manager.visibleConditions.value,
              onAdded: (addedItem) => makeFilterGroup(index, <ConditionViewModel>item, addedItem),
              onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
            }, {
              default: _withCtx(() => [
                _createElementVNode("button", {
                  class: "button add-icon",
                  disabled: __props.disabled
                }, [
                  _createVNode(AddIcon)
                ], 8, _hoisted_3)
              ]),
              _: 2
            }, 1032, ["disabled", "tooltip", "available-conditions", "onAdded"]))
          : _createCommentVNode("", true),
        (showAddToFilterGroupOption(item, index))
          ? (_openBlock(), _createBlock(AddCondition, {
              key: 3,
              "available-conditions": __props.manager.visibleConditions.value,
              disabled: __props.disabled || hasAnyInvalidConditions(),
              tooltip: 
          !hasAnyInvalidConditions() ? `Add to ${__props.context} group` : `Fix invalid ${__props.context}s before adding another`
        ,
              onAdded: (addedItem) => addToFilterGroup(index, addedItem)
            }, {
              default: _withCtx(() => [
                _createElementVNode("button", {
                  class: "button add-icon",
                  disabled: __props.disabled
                }, [
                  _createVNode(AddIcon)
                ], 8, _hoisted_4)
              ]),
              _: 2
            }, 1032, ["available-conditions", "disabled", "tooltip", "onAdded"]))
          : _createCommentVNode("", true),
        (!isLastConditionInList(index))
          ? (_openBlock(), _createBlock(JoinOperatorPicker, {
              key: 4,
              class: _normalizeClass(["group-type", { 'top-level-grouping': !__props.nested, hover: operatorButtonHover.value }]),
              disabled: __props.disabled,
              value: conditionGroup.value.type,
              onUpdatedOperator: operatorTypeUpdated,
              onUpdatedHoverState: updateOperatorHoverState
            }, null, 8, ["class", "disabled", "value"]))
          : _createCommentVNode("", true)
      ]))
    }), 128)),
    (!__props.nested)
      ? (_openBlock(), _createBlock(AddCondition, {
          key: 0,
          "available-conditions": __props.manager.visibleConditions.value || [],
          disabled: __props.disabled || hasAnyInvalidConditions(),
          onAdded: onConditionAdded,
          class: _normalizeClass(["add-filter", { 'has-conditions': numberOfConditions.value > 0 }])
        }, {
          default: _withCtx(() => [
            _createElementVNode("a", null, [
              _createVNode(AddIcon),
              _createElementVNode("span", _hoisted_5, "Add " + _toDisplayString(__props.context), 1)
            ])
          ]),
          _: 1
        }, 8, ["available-conditions", "disabled", "class"]))
      : _createCommentVNode("", true)
  ]))
}
}

})